import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
// import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import { Row, Col } from 'react-bootstrap';
import { motion } from "framer-motion";
import Modal from 'react-bootstrap/Modal';
import "./index.scss";

import ProfileAvatar from "../../../../../components/ui/ProfileAvatar";
import NoDataFound from "../../../../../components/ui/NoDataFound";
import PaginationArrow from "../../../../../components/ui/PaginationArrow";
import Button from "../../../../../components/ui/Button";
import RadioButton from "../../../../../components/ui/RadioButton";
import SkeletonLoader from "../../../../../components/ui/SkeletonLoader";
import OverflowText from "../../../../../components/ui/Textoverflow";

import SSLCertificate from "../../../../../components/PostLogin/ScanInfo/ExposureManagement/AssetDiscovery/AssociatedDomain/SSLCertificate";
import EndPoints from "../../../../../components/PostLogin/ScanInfo/ExposureManagement/AssetDiscovery/Technologies/EndPoints";

import { filterIcon, searchIcon } from "../../../../../utils/icons";
import { formatDate, formatNumber, getSSLIssuerImageUrl } from "../../../../../utils/util";

import { useScanInfoContext } from "../../../../ScanInfo/ScanInfoContext";

import { useSelector, useDispatch } from 'react-redux';
import { getSSLCerificateList, getScanInfoStats, resetScanInfoStats, resetSSLCertificateList } from "../../../../../store/actions/scanInfoAction";

import apiEndpoints from "../../../../../apiEndpoints";

const newFindingsOption = [    
    {
        label: "Yes",
        value: "yes"
    },
    {
        label: "No",
        value: "no"
    },
    {
        label: "All",
        value: "all"
    }
]

const SSLRecords = () => {

    const sslCertificateListResponse = useSelector((state) => state.scanInfo?.sslCertificates);
    const sslRecordStatsResponse = useSelector((state) => state.scanInfo?.scanInfoStats);

    const filterWrapperRef = useRef(null);

    const dispatch = useDispatch();

    const {
        domainId,
        scanId,
        organizationId,
        triggerStatsFetch,
        setTriggerStatsFetch,
        triggerListFetch,
        setTriggerListFetch,
        setContextCurrentPage
    } = useScanInfoContext();

    const [activeTab, setActiveTab] = useState("sslInformation");
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTechnology, setSelectedTechnology] = useState("");
    const [scanNotFound, setScanNotFound] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [sslRecordStats, setSSLRecordStats] = useState("");
    const [showViewAllModal, setShowViewAllModal] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [sslCertificateList, setSSLCertificateList] = useState(null);

    const [sslRecordSearch, setSslRecordSearch] = useState("");
    const [updatedSslRecordSearchValue, setUpdatedSslRecordSearchValue] = useState("");

    //filter value
    const [showFilter, setShowFilter] = useState(false);
    const [newFindingsType, setNewFindingsType] = useState("");
    const [selectedExpired, setSelectedExpired] = useState("")

    //selected filter value
    const [selectedFilterValue, setSelectedFilterValue] = useState({});
    
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        setContextCurrentPage(newPage);
        setTriggerListFetch(true);
    };

    const applyFilter = () => {
        setCurrentPage(1);        
        setShowFilter(false);
        let obj = {
            findings: newFindingsType,
            expired: selectedExpired
        }
        setSelectedFilterValue(obj);
        setContextCurrentPage(1);
        setTriggerListFetch(true);
    }

    const isAnyFilterActive = Object.values(selectedFilterValue).some(value => Boolean(value));

    const resetFilterValue = () => {
        setCurrentPage(1);
        setSelectedFilterValue({});
        setNewFindingsType("");
        setSelectedExpired("");
        setContextCurrentPage(1);
        setTriggerListFetch(true);
    }

    const fetchSSLRecordStats = useCallback(()=>{
        setStatsLoading(true)
        const baseUrl = `${apiEndpoints.scanInfo.exposureManagemnet.assetDiscovery.sslCertificate.sslRecordStats()}`
        const params = organizationId.toLowerCase() !== "all" && domainId.toLowerCase() === "all" ? `&organization_id=${organizationId}`: `&domain=${domainId}&scan_id=${scanId}`
        const url = `${baseUrl}${params}`
        dispatch(getScanInfoStats(url));
    },[dispatch, domainId, scanId, organizationId])

    const requestSearch = useMemo(() => debounce((searchTerm) => {        
        setUpdatedSslRecordSearchValue(searchTerm)
    }, 500), []);


    const fetchSSLCertificateList = useCallback(()=>{
        const baseUrl = `&page=${currentPage}&page_size=10`
        const params = organizationId.toLowerCase() !== "all" && domainId.toLowerCase() === "all" ? `&organization_id=${organizationId}`: `&domain=${domainId}&scan_id=${scanId}`
        const queryparams = updatedSslRecordSearchValue ? `&query=${updatedSslRecordSearchValue}` : "";
        const newFindings = selectedFilterValue.findings && selectedFilterValue.findings !== "all" ? `&new_record=${selectedFilterValue.findings === "yes" ? true : false}` : "";
        const expired = selectedFilterValue.expired && selectedFilterValue.expired !== "all" ? `&expired=${selectedFilterValue.expired === "yes" ? true : false}` : "";        
        const url = `${baseUrl}${params}${queryparams}${newFindings}${expired}`
        setIsLoading(true);
        setScanNotFound(false);
        dispatch(getSSLCerificateList(url))
    },[dispatch, domainId, scanId, organizationId, currentPage, updatedSslRecordSearchValue, selectedFilterValue])

    useEffect(() => {
        if (triggerStatsFetch) {
            fetchSSLRecordStats();
            setCurrentPage(1)
            setContextCurrentPage(1)
            setTriggerStatsFetch(false);
        }
    }, [triggerStatsFetch, setContextCurrentPage, setTriggerStatsFetch, fetchSSLRecordStats]);

    useEffect(() => {
        if (triggerListFetch) {
            fetchSSLCertificateList();
            setTriggerListFetch(false);
        }
    }, [triggerListFetch, currentPage, setTriggerListFetch, fetchSSLCertificateList]);

    useEffect(() => {
        setCurrentPage(1); // reset to first page
        setContextCurrentPage(1);
        setTriggerListFetch(true); // trigger fetch
    }, [setTriggerListFetch, setContextCurrentPage, updatedSslRecordSearchValue]);

    useEffect(()=>{
        if(sslCertificateListResponse !== null) {
            setSSLCertificateList(sslCertificateListResponse)
            if(sslCertificateListResponse?.status === "failed") {
                setScanNotFound(true)
            } else {
                setScanNotFound(false)
            }
            if(sslCertificateListResponse?.results?.length > 0) {                
                setSelectedTechnology(sslCertificateListResponse?.results[0])
            }
            dispatch(resetSSLCertificateList())
            setIsLoading(false);
        }
    },[sslCertificateListResponse, dispatch])

    useEffect(()=>{
        if(sslRecordStatsResponse !== null) {
            setStatsLoading(false)
            setSSLRecordStats(sslRecordStatsResponse)            
            dispatch(resetScanInfoStats())
        }
    },[sslRecordStatsResponse, dispatch])

    useEffect(()=>{
        document.addEventListener("mousedown", handleClickOutside);

        function handleClickOutside(event) {
            if (filterWrapperRef.current && !filterWrapperRef.current.contains(event.target)) {
                if(showFilter){
                    setShowFilter(false)
                }
            }
        
        }
    },[showFilter])


    return (
        <>
            <div className="scan-info-main-content">
                <div className="bg-white-with-shadow p-4">
                    <Row>
                        <Col lg={4}>
                            {
                                statsLoading ?
                                <SkeletonLoader type={"line"} count={3} currentPage={"sslRecord"} />
                                :
                                <motion.div
                                    className="h-100"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4 }}
                                >
                                    <div className="discover-doamin-container associated-doamin-page rounded p-3 h-100">
                                        <div className="header-content d-flex justify-content-between">
                                            <p className="heading">SSL Record</p>
                                        </div>
                                        <div className="d-flex gap-3">
                                            <div className="total-score-container rounded">
                                                <div className="users-totol-count d-flex">
                                                    <p className="total-score-count">{formatNumber(sslRecordStats?.record_type?.total)}</p>
                                                    <div className="score-count mx-3">
                                                        <span className="new-record d-inline-block mx-1"></span>
                                                        <span className="existing-record d-inline-block"></span>
                                                        <p>Total Records</p>
                                                    </div>
                                                </div>
                                                <div className="new-exist-count d-flex gap-5 mt-3">
                                                    <p className="new-record"><span className="d-inline-block">{formatNumber(sslRecordStats?.record_type?.new_record)}</span> New</p>
                                                    <p className="existing-record"><span className="d-inline-block">{formatNumber(sslRecordStats?.record_type?.existing_record)}</span> Existing</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            }                            
                        </Col>
                        <Col lg={4}>
                            {
                                statsLoading ?
                                <SkeletonLoader type={"line"} count={3} currentPage={"sslRecord"} />
                                :
                                <motion.div
                                    className="h-100"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4 }}
                                >
                                    <div className="discover-doamin-container associated-doamin-page ports-container rounded p-3 h-100">
                                        <div className="header-content d-flex justify-content-between">
                                            <p className="heading">SSL Expiry</p>
                                        </div>
                                        <div className="d-flex gap-3">
                                            <div className="total-score-container rounded">
                                                <div className="users-totol-count d-flex">
                                                    <p className="total-score-count">{formatNumber(sslRecordStats?.ssl_expiry_type?.total)}</p>
                                                    <div className="score-count mx-3">
                                                        <span className="new-record d-inline-block mx-1"></span>
                                                        <span className="existing-record d-inline-block"></span>
                                                        <p>Total Ports</p>
                                                    </div>
                                                </div>
                                                <div className="new-exist-count d-flex gap-3 mt-3">
                                                    <p className="new-record"><span className="d-inline-block">{formatNumber(sslRecordStats?.ssl_expiry_type?.["30_days"])}</span> &lt; 30 days</p>
                                                    <p className="existing-record"><span className="d-inline-block">{formatNumber(sslRecordStats?.ssl_expiry_type?.["60_days"])}</span> &lt; 60 days</p>
                                                    <p className="existing-record"><span className="d-inline-block">{formatNumber(sslRecordStats?.ssl_expiry_type?.["90_days"])}</span> &lt; 90 days</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            }                            
                        </Col>
                        <Col lg={4}>
                            {
                                statsLoading ?
                                <SkeletonLoader type={"line"} count={3} currentPage={"sslRecord"} />
                                :
                                <motion.div
                                    className="h-100"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4 }}
                                >
                                    <div className="sub-domain-type-container web-server-type-container rounded p-3 h-100">
                                        <div className="header-content d-flex justify-content-between">
                                            <p className="heading">SSL Issuer</p>
                                            {sslRecordStats !== "" && sslRecordStats?.ssl_issuer_type?.records && Object.entries(sslRecordStats?.ssl_issuer_type?.records).length > 3 && <p className="view-all" onClick={()=>{setShowViewAllModal(true)}}>View all</p>}
                                        </div>
                                        <div className="total-records-container">
                                            <p className="content">{formatNumber(sslRecordStats?.ssl_issuer_type?.total)} <span>Total Records</span></p>
                                        </div>
                                        <div className="sub-domain-content">
                                            <Row className="mt-3">
                                                {
                                                    sslRecordStats !== "" && sslRecordStats?.ssl_issuer_type?.records && Object.entries(sslRecordStats?.ssl_issuer_type?.records).length > 0 &&
                                                    Object.entries(sslRecordStats?.ssl_issuer_type?.records).map(([key, value], index) => {
                                                        if(index <= 2) {
                                                            return (
                                                                <Col lg={4} key={index}>
                                                                    <div className="d-flex align-items-center gap-3 ssl-record-profile">
                                                                        <ProfileAvatar type="profile" firstName={formatNumber(value)} lastName="" secondaryText={key} imageUrl={getSSLIssuerImageUrl(key)} />
                                                                    </div>
                                                                </Col>
                                                            )
                                                        }
                                                        return null                                           
                                                    })
                                                }
                                            </Row>
                                        </div>
                                    </div>
                                </motion.div>   
                            }                   
                        </Col>
                    </Row>
                </div>
                <div className="mt-4">
                    <Row>
                        <Col lg={3}>
                            <div className="bg-white-with-shadow subdomain-list-container h-100">
                                <div className="list-heading px-3 pt-3 d-flex justify-content-between align-items-center">
                                    <h1>Certificate List</h1>
                                    {isAnyFilterActive && (<span className="reset-filter-link" onClick={resetFilterValue}>Reset Filter</span>)}
                                    <div className="position-relative">
                                        <span className="filter-icon" onClick={()=>{setShowFilter(true)}}>{filterIcon()}</span>
                                        {showFilter && <div className="filters-container position-absolute p-3 rounded" ref={filterWrapperRef}>
                                            <h6>New Findings</h6>
                                            <div className="filter-user-status d-flex mb-4 gap-4">
                                                {
                                                    newFindingsOption.map((data,index)=>{
                                                        return (
                                                            <RadioButton radioId={data.label + index} radioValue={data.value} radioLabel={data.label} onChange={(value)=>{setNewFindingsType(value)}} key={index} isChecked={data.value === newFindingsType} />
                                                        )
                                                    })
                                                }
                                            </div>
                                            <h6>Expired</h6>
                                            <div className="filter-user-status d-flex mb-4 gap-4">
                                                {
                                                    newFindingsOption.map((data,index)=>{
                                                        return (
                                                            <RadioButton radioId={`expired-${index}`} radioValue={data.value} radioLabel={data.label} onChange={(value)=>{setSelectedExpired(value)}} key={index} isChecked={data.value === selectedExpired} />
                                                        )
                                                    })
                                                }
                                            </div>
                                            <div className="filter-apply-btn mt-3">
                                                <Button buttonText={"Apply"}  buttonType={"primary"} disableButton={newFindingsType === "" && selectedExpired === ""} buttonClicked={()=>{applyFilter()}}/>
                                            </div>
                                        </div>}
                                    </div>
                                </div>
                                <div className="search-container px-3 position-relative">
                                    <span className="position-absolute search-icon">{searchIcon()}</span>
                                    <input type="text" 
                                        placeholder="Search" 
                                        className="w-100 rounded input" 
                                        value={sslRecordSearch} 
                                        onChange={(e)=>{                                            
                                            setSslRecordSearch(e.target.value)
                                            requestSearch(e.target.value)
                                            setCurrentPage(1);
                                            setContextCurrentPage(1);
                                            setTriggerListFetch(true);
                                        }}
                                    />
                                </div>
                                <div className="list-main-container">
                                    { !isLoading && scanNotFound &&
                                        <NoDataFound noDataText={sslCertificateList?.description}/>
                                    }
                                    {
                                        isLoading && !scanNotFound ?
                                        <SkeletonLoader type={"line"} count={5} currentPage={"sslRecord"} />
                                        :
                                        (
                                            sslCertificateList?.results?.length > 0 &&
                                            sslCertificateList?.results?.map((data, index) => {
                                                return (
                                                    <motion.div
                                                        key={index}
                                                        initial={{ opacity: 0, y: 20 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.4, delay: index * 0.1 }}
                                                    >
                                                        <div className={`sub-domain-list py-2 px-3 ${selectedTechnology?.id === data?.id ? "active" : ""}`} onClick={()=>{setSelectedTechnology(data)}}>
                                                            <div className={`d-flex align-items-center subdomain-name gap-2 mb-2 ${!data?.new_record ? "full-width" : ""}`}>
                                                                <OverflowText text={data?.fingerprints?.sha256 !== null ? data?.fingerprints?.sha256 : data?.fingerprints?.sha1} className={"sub-domain-heading"}/>
                                                                {/* <p className="sub-domain-heading">{data?.fingerprints?.sha256 !== null ? data?.fingerprints?.sha256 : data?.fingerprints?.sha1}</p> */}
                                                                {data.new_record && <span className="new-subdomain">New</span>}
                                                            </div>
                                                            <p className="sub-domain-name">{data?.domain} | {formatDate(data?.created_at)}</p>
                                                            {data?.expired && <span className="subdomain-status blacklisted">Expired</span>}
                                                            {data?.self_signed && <span className="subdomain-status blacklisted">Self Signed</span>}
                                                        </div>
                                                    </motion.div>
                                                )
                                            })
                                        )
                                    }
                                    { !isLoading && sslCertificateList?.results?.length > 0 &&
                                        <PaginationArrow 
                                            totalCount={sslCertificateList?.meta?.total_results}
                                            currentPage={currentPage}
                                            onPageChange={handlePageChange}
                                        />                                  
                                    }
                                    { !isLoading && sslCertificateList?.results?.length === 0 &&
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.4 }}
                                        >
                                            <NoDataFound noDataText={"SSL Record Not Found"}/>
                                        </motion.div>                                        
                                    }
                                </div>
                            </div>
                        </Col>
                        <Col lg={9}>
                            <div className="bg-white-with-shadow sub-domain-main-container p-3 h-100">
                                <div className="tab-container d-flex rounded">
                                    <p className={`rounded text-center ${activeTab === "sslInformation" ? "active" : ""}`} onClick={()=>{setActiveTab('sslInformation')}}>SSL Certificate Information</p>
                                    <p className={`rounded text-center ${activeTab === "endpoints" ? "active" : ""}`} onClick={()=>{setActiveTab('endpoints')}}>Endpoints</p>
                                </div>
                                <div className="tab-details-content mt-3">
                                    {
                                        !isLoading && scanNotFound ?
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.4 }}
                                        >
                                            <NoDataFound noDataText={sslCertificateList?.description}/>
                                        </motion.div>
                                        
                                        :
                                        ( !isLoading && sslCertificateList?.results?.length === 0 ?
                                            <motion.div
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.4 }}
                                            >
                                                <NoDataFound noDataText={"No Data Found"}/>
                                            </motion.div>
                                            :
                                            <>
                                                {activeTab === "sslInformation" && <SSLCertificate sslCertificateData={selectedTechnology} isLoading={isLoading} />}
                                                {activeTab === "endpoints" && <EndPoints currentPageType={"sslRecord"} selectedTechnology={selectedTechnology} domainId={domainId} scanId={scanId}/>}
                                            </>
                                        )
                                    }                                    
                                </div>
                            </div>
                        </Col>
                    </Row>
                    <Modal
                        show={showViewAllModal}
                        onHide={()=>setShowViewAllModal(false)}
                        size="md"
                        centered
                        className=""
                        >
                            <Modal.Body>
                                <div className="delete-organization-container">
                                    <h4>SSL Issuer</h4>
                                    <div className="p-2 m-2 type-list-container">
                                        <div className="mt-3">
                                            {
                                                sslRecordStats !== "" && sslRecordStats?.ssl_issuer_type?.records && Object.entries(sslRecordStats?.ssl_issuer_type?.records).length > 0 &&
                                                Object.entries(sslRecordStats?.ssl_issuer_type?.records).map(([key, value], index) => {
                                                    return (
                                                        <motion.div
                                                            key={index}
                                                            initial={{ opacity: 0, y: 20 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            transition={{ duration: 0.4, delay: index * 0.1 }}
                                                        >
                                                            <div key={index}>
                                                                <div className="d-flex align-items-center justify-content-between gap-3 p-3 border-bottom scan-info-type-modal">
                                                                    <ProfileAvatar type="profile" firstName={key} lastName="" imageUrl={getSSLIssuerImageUrl(key)} />
                                                                    <p className="count-type total-count">{formatNumber(value)}</p>
                                                                </div>
                                                            </div>
                                                        </motion.div>
                                                    )                                        
                                                })
                                            }
                                        </div> 
                                    </div>
                                    <div className="d-flex justify-content-center gap-3 mt-4">
                                        <div className="w-50">
                                            <Button buttonText="Close" buttonType={"secondary"} buttonClicked={()=>setShowViewAllModal(false)}/>
                                        </div>
                                    </div>
                                </div>
                            </Modal.Body>
                    </Modal>
                </div>
            </div>
        </>
        
    )
}


export default SSLRecords;